import React, { useState, useEffect, useRef } from "react";
import styled, { ThemeProvider } from 'styled-components';
import { useTranslation } from "react-i18next";
import {LanguageToggler} from "apps/client/src/components/LanguageToggler/LanguageToggler";
import { useDarkMode } from "apps/client/src/context/darkModeContext";
import SelectBox from "apps/client/src/components/Select/box";
import styles from './result.module.scss'
import classNames from "classnames";
import { faCog } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { Button } from 'apps/client/src/components/Buttons';

interface Props {
  logo?: string,
  customText?: string,
  namespaceId?: string,
}

// Namespaces that receive the detailed 'survey-completed' postMessage (answers + scores)
export const DETAILED_POSTMESSAGE_NAMESPACES = ['experian', 'new-york-life', 'delete-thecat'];

const darkTheme = {
  backgroundColor: "#2a2a2a", // Dark background color
  textColor: "#ffffff", // Text color in dark mode
};

const lightTheme = {
  backgroundColor: "#ffffff", // Light background color
  textColor: "#2a313e", // Text color in light mode
};

const SurveyResult: React.FC<Props> = ({ logo, customText, namespaceId }) => {
  const { t } = useTranslation();
  const {isDarkMode, toggleDarkMode} = useDarkMode();

  const mode = [{ id: "light", text: "Light Mode" }, { id: "dark", text: "Dark Mode" }]

  const [dropdownVisible, setDropdownVisible] = useState(false);

  const toggleDropdown = () => {
    setDropdownVisible(!dropdownVisible);
  };

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownVisible(false);
      }
    };

    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    // Cleanup the event listeners on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [dropdownRef]);

  useEffect(() => {
    // Post message to parent window when survey is completed
    if (DETAILED_POSTMESSAGE_NAMESPACES.includes(namespaceId ?? '')) return;
    window.top?.postMessage({ type: 'survey-completed' }, '*');
  }, []);

  const Image = styled.img`
    width: 320px;
    height: auto;
    margin-bottom: 25px;
  `;

  const Title = styled.h1`
    font-size: 24px;
    font-weight: 400;
    height: fit-content;
    color: ${props => props.theme.textColor};
    letter-spacing: -0.03em;
    line-height: 1.4em;
    text-align: center;
    white-space: pre-wrap;
  `;

  const Wrapper = styled.div`
    width: 100%;
    height: 100vh;
    background-color: ${props => props.theme.backgroundColor};
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    position: relative;
  `;

  const handleExit = () => {
    window.top?.postMessage({ type: 'survey-exit' }, '*');
  };

  const sanitizeText = (text: string): string => {
    return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
  };

  return (
    <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
      <div>
        {namespaceId !== "experian" && (
          <div
            className={classNames(
              styles.cogButton,
              isDarkMode && styles.darkMode
            )}
          >
            <button
              onClick={toggleDropdown}
              className={classNames(
                styles.button,
                isDarkMode && styles.darkMode,
                dropdownVisible && styles.activeDropdown
              )}
            >
              <FontAwesomeIcon icon={faCog} />
            </button>
            {dropdownVisible && (
              <div
                ref={dropdownRef}
                className={classNames(
                  styles.dropdownMenu,
                  isDarkMode && styles.darkMode
                )}
              >
                <SelectBox
                  data={mode}
                  value={isDarkMode ? "dark" : "light"}
                  defValue={isDarkMode ? "dark" : "light"}
                  onChange={() => toggleDarkMode()}
                  className={classNames(
                    styles.selectBox,
                    isDarkMode && styles.darkMode
                  )}
                />
                <LanguageToggler className={classNames(
                    styles.selectBox,
                    isDarkMode && styles.darkMode
                  )}/>
              </div>
            )}
          </div>
        )}

        <Wrapper>
          <Image src={logo} />
          <Title>
           {customText
           ? parse(sanitizeText(customText))
           : parse(sanitizeText(t("results.becu.title")))}
          </Title>
          {namespaceId === "experian" && (
            <div style={{ marginTop: '30px' }}>
              <Button
                variant="primary"
                size="lg"
                className={styles.resultButton}
                onClick={handleExit}>
                {t("results.exit")}
              </Button>
            </div>
          )}
        </Wrapper>
      </div>
    </ThemeProvider>
  );
};

export default SurveyResult;